/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatDateBody } from 'Helpers/date-utils';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkHealthSummary } from 'Redux/modules/ai/actions';
import { RootState } from 'Redux/reducer';

import { AI_CHECKER_GA_EVENT } from '../../AINotes/constants';

import useAiCheckerLogEvent from './useAiCheckerLogEvent';

export interface ICheckHealthSummaryResponse {
  id: string;
  score: number;
  status: string;
  consultId: string;
  doctorId: string;
  summary: {
    chiefComplaintAndHistoryOfPresentIllness: {
      quotedNote: string;
      rating: string;
      score: number;
      justification: string;
      flaws: string[];
    };
    pastMedicalHistory: {
      quotedNote: string;
      rating: string;
      justification: string;
      score: number;
    };
    allergiesAndAdverseDrugReactions: {
      quotedNote: string;
      rating: string;
      justification: string;
      flaws: string[];
      score: number;
    };
    physicalVisualFindings: {
      quotedNote: string;
      rating: string;
      justification: string;
      flaws: string[];
      score: number;
    };
    assessment: {
      quotedNote: string;
      rating: string;
      justification: string;
      flaws: string[];
      score: number;
    };
    planOfCare: {
      quotedNote: string;
      rating: string;
      justification: string;
      flaws: string[];
      score: number;
    };
    followUpInstructions: {
      quotedNote: string;
      rating: string;
      justification: string;
      flaws: string[];
      score: number;
    };
    overallNote: {
      mandatoryElement: string;
      supplementalHighlights: {
        currentPastMedications: string;
        problemList: string;
        reviewOfSystems: string;
        socialAndFamilyHistory: string;
      };
      commendations: string[];
      suggestionsForImprovement: string[];
    };
  };
  userFeedback: string;
  userNote: string;
  rank: string;
}

const generatePrompt = (summary: any) => {
  const { clinicalNotes, summary: diagnosis, plan, followUpDate, privateNote } = summary;
  const date = followUpDate ? formatDateBody(followUpDate) : '';
  const prompt = `Diagnosis: ${diagnosis ?? ''} \n Clinical Notes: ${
    clinicalNotes ?? ''
  } \n Plan: ${plan ?? ''}  \n Follow-up Visit: ${date ?? ''} \n Note: ${privateNote ?? ''}`;

  return prompt;
};

const useCheckHealthSummary = () => {
  const dispatch = useDispatch();

  const aiStore = useSelector((state: RootState) => state.ai) as any;

  const { checkHealthSummary: data, status } = aiStore;

  const isLoading = status?.checkHealthSummary === 0 || data?.status === 'processing';

  const { logAICheckerEvent } = useAiCheckerLogEvent(data?.score);

  const handleCheckHealthSummary = useCallback(
    (summary, consultId, source = 'Finish consult') => {
      const prompt = generatePrompt(summary);
      dispatch(
        checkHealthSummary({
          consultId,
          prompt,
        }),
      );
      logAICheckerEvent(AI_CHECKER_GA_EVENT.AI_CHECKER_TRIGGER, {
        consult_id: consultId,
        source,
      });
    },
    [dispatch, logAICheckerEvent],
  );

  return {
    handleCheckHealthSummary,
    data: data as ICheckHealthSummaryResponse,
    isLoading,
  };
};

export default useCheckHealthSummary;
